/**
 * menu.ts — sidebar navigation definition.
 *
 * Phase 2 ships the analytics dashboard. The sidebar now has TWO sections:
 *
 *   DASHBOARD   — Overview + the 6 analytics pages (Loans, Deposits, NPA,
 *                 Products, Branches, Data Quality)
 *   OPERATIONS  — the Phase 1 Bank Submission Status page
 *
 * Drill-in pages (Bank Detail, Branch Detail, Product Detail) intentionally
 * have no menu entry — they're entered via row-click navigation.
 */

export interface MenuSection {
	section: string;
}

export interface MenuItem {
	k: string;
	label: string;
	icon: string;
	path: string;
	badge?: 'NEW' | 'LIVE';
	/** Match an active state if the URL prefix matches (for drill-ins). */
	activePrefix?: string;
}

export type MenuEntry = MenuSection | MenuItem;

export const MENU: MenuEntry[] = [
	{ section: 'Dashboard' },
	{ k: 'overview', label: 'Overview', icon: 'home', path: '/' },
	{
		k: 'banks',
		label: 'Banks',
		icon: 'shield',
		path: '/banks',
		// Match /banks (list), /banks/:orgId (detail), /banks/:orgId/branches/:id (branch detail)
		// but NOT /banks/submission-status which lives under Operations.
		activePrefix: '/banks',
	},
	{ k: 'loans', label: 'Loans', icon: 'coins', path: '/loans' },
	{ k: 'deposits', label: 'Deposits', icon: 'wallet', path: '/deposits' },
	{ k: 'npa', label: 'NPA Analysis', icon: 'warning', path: '/npa' },
	{ k: 'products', label: 'Product Analytics', icon: 'book', path: '/products', activePrefix: '/products' },
	{ k: 'branches', label: 'Branches', icon: 'users', path: '/branches', activePrefix: '/branches' },
	{ k: 'data-quality', label: 'Data Quality', icon: 'shield', path: '/data-quality' },

	{ section: 'Operations' },
	{
		k: 'bank-submission-status',
		label: 'Bank Submission Status',
		icon: 'shield',
		path: '/banks/submission-status',
		badge: 'LIVE',
	},
];

export const PAGE_TITLES: Record<string, [string, string]> = {
	overview: ['Dashboard', 'Overview'],
	banks: ['Dashboard', 'Banks'],
	loans: ['Dashboard', 'Loans'],
	deposits: ['Dashboard', 'Deposits'],
	npa: ['Dashboard', 'NPA Analysis'],
	products: ['Dashboard', 'Product Analytics'],
	branches: ['Dashboard', 'Branches'],
	'data-quality': ['Dashboard', 'Data Quality'],
	'bank-detail': ['Dashboard', 'Bank Detail'],
	'branch-detail': ['Dashboard', 'Branch Detail'],
	'product-detail': ['Dashboard', 'Product Detail'],
	'bank-submission-status': ['Operations', 'Bank Submission Status'],
};

export function isSection(e: MenuEntry): e is MenuSection {
	return 'section' in e;
}
